import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "api";
import { T_Api_Paginated, T_Api_Success_Res } from "./api.types";
import { MiscFile, MiscFileType } from "dtos/misc.file.dto";

export const miscFilesService = {
  useGetMiscFiles: ({
    page,
    limit,
    search,
    type,
    sortBy,
    sortOrder,
    referenceId,
    mode,
    loanId,
    creditorId,
  }: {
    page: number;
    limit: number;
    search?: string;
    type?: MiscFileType;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    referenceId?: string;
    mode?: string;
    loanId?: string;
    creditorId?: string;
  }) => {
    return useQuery({
      queryKey: ["misc-files", page, limit, search, type, sortBy, sortOrder, referenceId, mode, loanId, creditorId],
      queryFn: async () => {
        const res = await api.get(`/misc-files/all`, {
          params: {
            page,
            limit,
            search,
            type,
            sort_by: sortBy,
            sort_order: sortOrder,
            reference_id: referenceId,
            mode,
            loan_id: loanId,
            creditor_id: creditorId,
          },
        });
        return res.data as T_Api_Success_Res<T_Api_Paginated<MiscFile>>;
      },
      enabled: !!referenceId && !!mode,
    });
  },

  useAddMiscFiles: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: ["misc-files/add"],
      mutationFn: async ({ formData }: { formData: FormData }) => {
        const res = await api.post(`/misc-files`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return res.data as T_Api_Success_Res<MiscFile[]>;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["misc-files"] });
      },
    });
  },

  useEditMiscFile: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: ["misc-files/edit"],
      mutationFn: async ({
        id,
        update,
      }: {
        id: string;
        update: Partial<Pick<MiscFile, "alias" | "notes" | "type" | "loan_id" | "creditor_id">>;
      }) => {
        const res = await api.patch(`/misc-files/${id}`, update);
        return res.data as T_Api_Success_Res<MiscFile>;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["misc-files"] });
      },
    });
  },

  useDeleteMiscFile: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: ["misc-files/delete"],
      mutationFn: async ({ id }: { id: string }) => {
        const res = await api.delete(`/misc-files/${id}`);
        return res.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["misc-files"] });
      },
    });
  },

  useGetMiscFileUrl: () => {
    return useMutation({
      mutationKey: ["misc-files/url"],
      mutationFn: async ({ id }: { id: string }) => {
        const res = await api.get(`/misc-files/${id}/url`);
        return res.data as T_Api_Success_Res<{ url: string }>;
      },
    });
  },
};
